/**
 * Motion tokens — the single source of truth for how this app moves.
 *
 * Reference: motion.dev. Two rules drive everything here.
 *
 *   1. Springs for anything that responds to a person (press, hover, a panel
 *      opening, a value changing). Easings for anything that responds to the
 *      system (a page arriving, a skeleton resolving). A spring carries
 *      momentum, which is what makes an interaction feel answered rather than
 *      merely scheduled.
 *   2. Short. This is a console someone uses forty times a day; motion is
 *      there to explain what changed, not to perform. Nothing here runs long
 *      enough to be waited on, and nothing blocks input.
 *
 * The easing arrays mirror the `--ease-*` custom properties in globals.css so
 * a CSS transition and a Motion animation on the same element agree.
 */

/** Cubic-bezier control points, matching `--ease-*` in globals.css. */
export const EASE = {
  /** Default. Fast départ, long settle — reads as "already happening". */
  out: [0.25, 1, 0.5, 1],
  /** Sharper still. For larger travel: page content, slide-overs. */
  expo: [0.16, 1, 0.3, 1],
  /** Symmetric. Only for things that leave and come back (crossfades). */
  inOut: [0.65, 0, 0.35, 1],
};

/**
 * Springs, by how much overshoot the gesture can carry.
 *
 * `snappy` is the workhorse: no visible bounce, but it decelerates like an
 * object rather than a clock. `bouncy` is reserved for confirmations — a
 * success check, a badge count ticking up — where a little overshoot reads as
 * delight instead of noise.
 */
export const SPRING = {
  /** Presses, hovers, small toggles. Effectively critically damped. */
  snappy: { type: 'spring', stiffness: 520, damping: 34, mass: 0.7 },
  /** Panels, drawers, layout shifts. Enough mass to feel like a surface. */
  smooth: { type: 'spring', stiffness: 320, damping: 34, mass: 0.9 },
  /** Confirmations only. */
  bouncy: { type: 'spring', stiffness: 460, damping: 20, mass: 0.8 },
};

/** Seconds — Motion's unit. Kept in step with `--duration-*` in globals.css. */
export const DURATION = {
  fast: 0.12,
  base: 0.18,
  slow: 0.28,
};

/** The default tween for non-gestural motion. */
export const TWEEN = { duration: DURATION.base, ease: EASE.out };
export const TWEEN_SLOW = { duration: DURATION.slow, ease: EASE.expo };

/* ---------------------------------------------------------------------------
   Variants

   Exported as objects rather than inline props so that a list and its items
   can share one definition, and so `initial`/`animate`/`exit` names stay
   consistent across every surface that uses them.
   ------------------------------------------------------------------------- */

/** Fade only. For things that must not move — text swaps, tab panels. */
export const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: TWEEN },
  exit: { opacity: 0, transition: { duration: DURATION.fast, ease: EASE.out } },
};

/**
 * The default entrance: 8px of upward travel under a fade.
 *
 * 8px is deliberate. Below ~6px the travel is invisible and you have paid for
 * an animation nobody sees; above ~14px on a grid of cards the page appears to
 * assemble itself, which is slow to watch on every navigation.
 */
export const fadeUp = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: TWEEN_SLOW },
  exit: { opacity: 0, y: -4, transition: { duration: DURATION.fast, ease: EASE.out } },
};

/** Entrance for overlay panels — modal, popover, toast. */
export const scaleIn = {
  hidden: { opacity: 0, scale: 0.97, y: 6 },
  visible: { opacity: 1, scale: 1, y: 0, transition: SPRING.smooth },
  exit: { opacity: 0, scale: 0.98, y: 4, transition: { duration: DURATION.fast, ease: EASE.out } },
};

/** Scrim behind a modal or drawer. */
export const scrim = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.base, ease: EASE.out } },
  exit: { opacity: 0, transition: { duration: DURATION.fast, ease: EASE.out } },
};

/** Right-hand slide-over. */
export const slideOver = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: SPRING.smooth },
  exit: { x: '100%', transition: { duration: DURATION.slow, ease: EASE.expo } },
};

/**
 * Parent variant for a stagger. Children opt in by declaring `hidden` /
 * `visible` themselves — Motion propagates the state down, so items need no
 * per-item delay prop and a list can be reordered without re-tuning it.
 *
 * 0.035s is about the shortest interval that still reads as a sequence. Longer
 * and a twelve-row table takes half a second to finish arriving.
 */
export function staggerParent(stagger = 0.035, delayChildren = 0) {
  return {
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger, delayChildren },
    },
    exit: {},
  };
}

/** Press feedback for a filled control. Paired with `SPRING.snappy`. */
export const PRESS = { scale: 0.97 };
/** Press feedback for a large surface (a card, a nav row) — subtler. */
export const PRESS_SOFT = { scale: 0.995 };
